import * as d3 from "d3";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { AiOutlineDownload, AiOutlineInfo } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import EvdaEuropeFilterWrapper from "../../components/filter-widget/evdaEuropeFilterWrapper";
import countriesJSON from "../../components/map-widget/europeData";
import MapWidget from "../../components/map-widget/mapWidget";
import TableComponent, { DataTable } from "../../components/table-widget/tableComponent";
import DataDisplaySwitcher from "../../components/templates/components/dataDisplaySwitcher";
import SmallFilterAccessor from "../../components/templates/components/smallFilterAccessor";
import PageTemplate from "../../components/templates/pageTemplate";
import { DropDownItem } from "../../models/DropDownItem";
import { SelectedArea } from "../../models/SelectedArea";
import { SelectedGender } from "../../models/SelectedGender";
import { countryDictionary } from "../../models/zoneDectionary";
import { getEuropeDisplayMode } from "../../redux/selectors/displayModeSelector";
import { getEvdaEuropeAgeFilterState, getEvdaEuropeCountriesFilterState, getEvdaEuropeDateFilterState, getEvdaEuropeGenderFilterState } from "../../redux/selectors/filterSelector";
import { DisplayModeAction } from "../../redux/states/displayModeState";
import { FilterAction } from "../../redux/states/filterState";
import { EuropeMapPoint, MapPoint } from "../../service/MapPoint";
import { filterMapPointsData, getMaxDate } from "../../service/dataFilter";
import getData from "../../service/dataLoader";
import { useWindowWidthAndHeight } from "../../shared/hooks/customHooks";
import { getRightAge } from "../../shared/utils/getRightAge";
import { GetCurveTitleForEVDAPage } from "../../shared/utils/getRightTitle";
import { EuropemapCenter, getGraduationClass, providePathClassName } from "../../shared/utils/mapsUtils";
import { convertNumberToString } from "../../shared/utils/numbersUtils";

export type SelectedItem = {
  name: string;
  key: string;
  esperance_de_vie: string;
  rang: string;
};

const EvdaEuropeWidget = () => {
  const [width, height] = useWindowWidthAndHeight();
  const ageFilter = useSelector(getEvdaEuropeAgeFilterState);
  const genderFilter = useSelector(getEvdaEuropeGenderFilterState);
  const dateFilter = useSelector(getEvdaEuropeDateFilterState);
  const countriesFilter = useSelector(getEvdaEuropeCountriesFilterState);
  const [EvdaEuropeWidgetData, setEvdaEuropeWidgetData] = useState<MapPoint[]>([]);
  const displayMode = useSelector(getEuropeDisplayMode);
  const [DataToDisplay, setDataToDisplay] = useState<MapPoint[]>([]);
  const [tooltipId, setTooltipId] = useState<string>(displayMode.isChartMode ? "tableDisplayMode" : "chartDisplayMode");
  const [mapTitleKey, setMapTitleKey] = useState<string | TemplateStringsArray | (string | TemplateStringsArray)[]>("evda_birth_curve_title");
  const [maxDate, setMaxDate] = useState<number>(0);
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("portrait");
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(FilterAction.SetArea(SelectedArea.Europe));
    getData(`en/outil-interactif/7344233/data/esperance_vie_europe.csv`).then((parsed) => {
      // Mapping de la propriété departement vers node
      const parsedData: MapPoint[] = parsed.data.map((mapPoint: EuropeMapPoint) => ({
        ...mapPoint,
        node: mapPoint.pays,
      }));

      // Set du jeu de données complet dans l'état du composant
      setEvdaEuropeWidgetData(parsedData);
    });
  }, []);

  // Use effect réagissant au changement de données global pour affecter la date maximale
  useEffect(() => {
    if (EvdaEuropeWidgetData.length > 0) {
      // Mise à jour de la date maximale fournie par le fichier
      setMaxDate(getMaxDate(EvdaEuropeWidgetData));
    }
  }, [EvdaEuropeWidgetData]);

  useEffect(() => {
    if (width > height) {
      setOrientation("landscape");
    } else {
      setOrientation("portrait");
    }
  }, [width, height]);

  // Use effect réagissant au changement de date maximale pour charger le premier jeu de données sur la carte
  useEffect(() => {
    if (EvdaEuropeWidgetData.length > 0 && maxDate > 0) {
      // Set par défaut de la date du filtre sur la date maximale
      if (dateFilter.selectedyear === 0) {
        // Cette action déclenche le useEffect qui écoute les states des filtres
        // filtrant initialement le jeu de données et affichant ainsi la carte
        dispatch(FilterAction.setEvdaEuropeSelectedYear(maxDate));
      }

      // Chargement initial des données sur la carte
      computeData();
    }
  }, [maxDate]);

  function computeData(): void {
    const result = filterMapPointsData(EvdaEuropeWidgetData, genderFilter.selectedGender, getRightAge(ageFilter.seletedAge, ageFilter.customSelectedAge), dateFilter.selectedyear);

    setDataToDisplay(result);
    setMapTitleKey(GetCurveTitleForEVDAPage(ageFilter.seletedAge, ageFilter.customSelectedAge, SelectedArea.Departments, genderFilter.selectedGender));
  }

  // use effect réagissant aux filtres pour mettre à jour le jeu de données
  useEffect(() => {
    computeData();
  }, [ageFilter, genderFilter.selectedGender, dateFilter.selectedyear]);

  const provideFilterWidget = () => {
    return <EvdaEuropeFilterWrapper maxDate={maxDate} data={DataToDisplay} />;
  };

  const provideSelectedCountries = (selected: DropDownItem[]) => {
    let countriesTosend: SelectedItem[] = [];
    if (selected.length > 0) {
      selected.forEach(function (country) {
        const found = DataToDisplay.filter((element) => element.node === country.Element.key);
        let evda = 'nd';
        let rang = -1;
        if (found && found.length > 0) {
          evda = DataToDisplay.filter((element) => element.node === country.Element.key)[0].esperance_de_vie;
          rang = DataToDisplay.filter((element) => element.node === country.Element.key)[0].rang;
        }
        countriesTosend.push({
          key: country.Element.key,
          name: t(country.Element.key),
          esperance_de_vie: evda,
          rang: rang?.toString(),
        });
      });
    }
    return countriesTosend;
  };

  const onSetDisplayModeEvent = () => {
    dispatch(DisplayModeAction.changeEuropeDisplayMode());
  };
  useEffect(() => {
    setTooltipId(displayMode.isChartMode ? "chartDisplayMode" : "tableDisplayMode");
  }, [displayMode.isChartMode]);

  const provideTableTitles = () => {
    let titles: string[] = [];
    titles.push(t("country"));
    if (genderFilter.selectedGender === SelectedGender.male) {
      titles.push(t("men"));
    } else if (genderFilter.selectedGender === SelectedGender.male_women_diff) {
      titles.push(t("gap"));
    } else {
      titles.push(t("women"));
    }

    titles.push(t("country_rank"));
    return titles;
  };

  const provideDataToTableDisplay = () => {
    let dataTable: DataTable[] = [];
    if (countriesFilter.selectedCountries.length === 0) {
      DataToDisplay.forEach(function (data) {
        const countryName = countryDictionary.filter((element) => element.key === data.node)[0].key;
        const element: DataTable = {
          key: "key" + data.node,
          columns: [
            { data: t(countryName), component: "th" },
            {
              data: data.esperance_de_vie !== "nd" && data.esperance_de_vie !== "NaN" ? convertNumberToString(parseFloat(data.esperance_de_vie), i18n, 1, 1) : t("value_not_available"),
            },
            {
              data: data.rang && data.rang > 0 ? data.rang.toString() : t("value_not_available"),
            },
          ],
        };
        dataTable.push(element);
      });
    } else {
      const filterResult = DataToDisplay.filter((e) => countriesFilter.selectedCountries.filter((country) => country.Element.key === e.node).length > 0);
      filterResult.forEach(function (data) {
        const countryName = countryDictionary.filter((element) => element.key === data.node)[0].key;
        const element: DataTable = {
          key: data.node,
          columns: [
            { data: t(countryName), component: "th" },
            {
              data: data.esperance_de_vie !== "nd" && data.esperance_de_vie !== "NaN" ? convertNumberToString(parseFloat(data.esperance_de_vie), i18n, 1, 1) : t("value_not_available"),
            },
            {
              data: data.rang && data.rang > 0 ? data.rang.toString() : t("value_not_available"),
            },
          ],
        };
        dataTable.push(element);
      });
    }
    return dataTable;
  };

  function projectionGetter(width: number, height: number): d3.GeoConicProjection {
    return (
      d3
        .geoConicConformal()
        // Coordonnées géographiques du centre de l'Europe
        // Permet de centrer l'Europe sur la carte
        .center(EuropemapCenter(width))
        // Facteur de zoom empirique pour projeter la carte avec un niveau de zoom humainement acceptable
        .scale(Math.min(width, height * 1.3))
        .translate([width / 2, height / 2])
    );
  }

  function drawNodes(path: d3.GeoPath<any, any>, deps: d3.Selection<SVGGElement, unknown, HTMLElement, any>, dataToDisplay: MapPoint[], selectedGender: SelectedGender): void {
    deps
      .selectAll("path")
      .data(countriesJSON.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("id", (d) => "d" + d.properties.ISO2)
      .attr("class", "country q0-5")
      .attr("class", (d) => {
        const e = dataToDisplay.find((data) => data.node === d.properties.ISO2.toString());
        return e !== undefined && getGraduationClass(dataToDisplay, parseFloat(e.esperance_de_vie)) !== undefined ? providePathClassName(selectedGender) + getGraduationClass(dataToDisplay, parseFloat(e.esperance_de_vie)) + "-5" : "country q0-5";
      });
  }

  const onClickCountryInMap = (item: DropDownItem) => {
    dispatch(FilterAction.setEvdaEuropeCountrySelection(item));
  };

  const provideMap = () => {
    const style1 = { color: "#ffffff" };
    const style2 = { color: "#0C3A5A" };

    return (
      <div className='display-data'>
        <Helmet>
          <title>{t('evda-europe-title')}</title>
        </Helmet>

        <div className='display-data-header'>
          <h3>{t('' + mapTitleKey, { year: dateFilter.selectedyear })}</h3>

          <div className='graph-button-container'>
            {width < 1150 ? <SmallFilterAccessor></SmallFilterAccessor> : undefined}

            <DataDisplaySwitcher
              tooltipId='displayMode'
              isChartMode={displayMode.isChartMode}
              setModeHandler={onSetDisplayModeEvent}
            ></DataDisplaySwitcher>

            <span
              aria-label="Informations supplémentaires sur les données d'espérance de vie en Europe"
              aria-details='infoTip'
              data-tip
              data-for='infoTip'
              className='info-round-button'
            >
              <AiOutlineInfo style={style2}></AiOutlineInfo>
            </span>

            <a
              href={`en/outil-interactif/7344233/data/downloads/Esperance_Vie_EUROPE.xlsx`}
              aria-label="Téléchargement des données complètes d'espérance de vie en Europe au format .xlsx"
              data-tip
              data-for='downloadTip'
              className='download-round-button'
            >
              <AiOutlineDownload style={style1}></AiOutlineDownload>
            </a>

            <ReactTooltip id='infoTip' place='bottom' effect='solid' multiline={true} html={true}>
              {t('tooltip-info-evda-departments')}
            </ReactTooltip>

            <ReactTooltip id='downloadTip' place='bottom' effect='solid' multiline={true} html={true}>
              {t('tooltip-download-evda-department')}
            </ReactTooltip>

            <ReactTooltip id='displayMode' place='bottom' effect='solid' multiline={true} html={true}>
              {
                // Maintenir cet variable permet de formcer le rechargement de la carte
                // Lors du changement de display mode (plantage sinon)
                tooltipId === 'chartDisplayMode' ? t('tooltip-change-display-table-mode') : t('tooltip-change-display-chart-mode')
              }
            </ReactTooltip>
          </div>
        </div>

        {displayMode.isChartMode ? (
          <MapWidget
            orientation={orientation}
            map={countriesJSON}
            nodeIdGetter={(feature) => feature.properties.ISO2.toString()}
            nodeNameGetter={(feature) => t(feature.properties.ISO2.toString())}
            projectionGetter={projectionGetter}
            drawNodes={drawNodes}
            dictionary={countryDictionary}
            itemClickHandler={onClickCountryInMap}
            selectedGender={genderFilter.selectedGender}
            data={DataToDisplay}
            selectedItems={provideSelectedCountries(countriesFilter.selectedCountries)}
            dontDisplayRegionKey={true}
          />
        ) : undefined}
        {!displayMode.isChartMode ? (
          <TableComponent
            caption={t('' + mapTitleKey, {
              age: ageFilter.customSelectedAge,
              unit: ageFilter.customSelectedAge === 1 ? 'an' : 'ans',
            })}
            unit={t('year_unit_plural', { postProcess: 'plural' })}
            data={provideDataToTableDisplay()}
            titles={provideTableTitles()}
          ></TableComponent>
        ) : undefined}
      </div>
    );
  };

  return <PageTemplate pageName="Europe" dataSource={t("evda_europe_data_source")} graphContent={provideMap()} filterContent={provideFilterWidget()} />;
};

export default EvdaEuropeWidget;
